'use client'

import { useState, useRef, useCallback, useMemo, useEffect, type DragEvent } from 'react'
import { toast } from 'sonner'
import { useFreemium } from '@/lib/freemium-context'
import { useIsMobile } from '@/hooks/use-mobile'
// `isLimitReached` n'est plus utilisé : le blocage gratuit dépend désormais
// du verrou `free_module_used` et du solde de crédits, calculé en local.
import { exportToWord } from '@/lib/export-word'
import { CreditCostBanner } from '@/components/CreditCostBanner'
import UpgradeBlockedCTA from '@/components/UpgradeBlockedCTA'
import {
  friendlyError,
  classifyUploadXhr,
  uploadFailureMessage,
  UploadFailureError,
  type UploadFailure,
} from '@/lib/error-messages'
import { AUTH_RESUME_SETTLE_MS } from '@/lib/auth-resume'
import {
  Upload, FileAudio, Loader2, Copy, Download, RotateCcw, Mic, Check,
  FileDown, Users, PencilLine, Save, ArrowRight, ShieldCheck, Paperclip, X, Clock, Info,
  Sparkles, HelpCircle,
} from 'lucide-react'
import { Textarea } from '@/components/ui/textarea'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog'

// ---------------------------------------------------------------------------
// Module constants
// ---------------------------------------------------------------------------

const M_COLOR = '#1a4a7a'
const M_BG = '#eaf0f8'
const M_STEPS = [
  { name: 'Upload audio', hint: 'Fichier audio/vidéo' },
  { name: 'Paramètres', hint: 'Infos entretien' },
  { name: 'Transcription', hint: 'Résultat & téléchargement' },
]

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ACCEPTED_EXTENSIONS = [
  '.mp3', '.mp4', '.wav', '.m4a', '.mov', '.avi', '.webm',
  // Formats ajoutés — tous décodés par ffmpeg puis normalisés en MP3 avant envoi
  '.aac', '.opus', '.m4r', '.flac', '.amr',
]
const ACCEPTED_MIME_TYPES = [
  'audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/x-wav', 'audio/wave', 'audio/x-m4a',
  'video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm',
  // MIME des formats ajoutés (le navigateur peut aussi renvoyer un type vide
  // pour certains — la validation par extension ci-dessus prend alors le relais)
  'audio/aac', 'audio/x-aac', 'audio/opus', 'audio/ogg',
  'audio/flac', 'audio/x-flac', 'audio/amr', 'audio/3gpp',
]

// Valeurs de l'attribut `accept` des inputs file. On combine les types
// génériques (audio/* , video/*) AVEC les extensions explicites : certains
// OS/navigateurs n'associent pas .aac/.opus/.amr/.flac à audio/* et les
// MASQUENT alors dans le sélecteur (constaté desktop ET mobile). Lister les
// extensions garantit qu'elles restent visibles/sélectionnables.
const AUDIO_PICK_EXTS = '.mp3,.wav,.m4a,.aac,.opus,.flac,.amr,.m4r'
const VIDEO_PICK_EXTS = '.mp4,.mov,.avi,.webm'
const ACCEPT_AUDIO = `audio/*,${AUDIO_PICK_EXTS}`
const ACCEPT_VIDEO = `video/*,${VIDEO_PICK_EXTS}`
const ACCEPT_ALL = `audio/*,video/*,${AUDIO_PICK_EXTS},${VIDEO_PICK_EXTS}`
const MAX_FILE_SIZE_MB = 2048
// Abaissé à 10 MB pour forcer le découpage des fichiers longs à faible bitrate
// (ex: audio 35 min à 64kbps ≈ 16 MB — dépasse la limite OpenAI de 25 MB
//  et serait tronqué silencieusement si envoyé directement).
const CHUNK_SIZE_BYTES = 10 * 1024 * 1024
// Seuil de DURÉE de bascule vers le découpage. gpt-4o-transcribe tronque
// silencieusement au-delà de ~25 min d'audio. Un fichier à faible débit
// (dictaphone, WhatsApp) peut rester sous 10 MB tout en dépassant 25 min :
// le seul critère de taille le laissait passer en route simple → fin perdue.
// Marge de sécurité : on bascule dès 20 min.
const CHUNK_DURATION_SEC = 20 * 60
const CONTEXT_ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.xlsx', '.xls', '.txt']
// accept = extensions + types MIME (le sélecteur mobile reconnaît mieux les
// documents et ne les grise pas). Validation inchangée (par extension).
const CONTEXT_ACCEPT_ATTR = [
  ...CONTEXT_ACCEPTED_EXTENSIONS,
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/plain',
].join(',')
const MAX_CONTEXT_FILE_SIZE_MB = 20
const MAX_CONTEXT_FILES = 5

// Fenêtre de l'historique récent affiché dans le module (millisecondes).
const HISTORY_WINDOW_MS = 48 * 60 * 60 * 1000

// ---------------------------------------------------------------------------
// Types & helpers
// ---------------------------------------------------------------------------

interface RecentTranscription {
  id: string
  file_name: string | null
  statut: string
  word_count: number | null
  created_at: string
  texte: string | null
  mode: string | null
}

/**
 * Sauvegarde/maj une transcription en base via /api/save-transcription.
 * Fonction pure (hors composant) → pas de souci d'ordre de déclaration des
 * hooks. Renvoie l'id de la ligne, ou null en cas d'échec (auto-save silencieux).
 * Si `id` est fourni, la ligne existante est mise à jour (pas de doublon).
 */
async function persistTranscription(params: {
  id?: string | null
  fileName: string
  fileSize: number
  mode: string
  texte: string
  statut: string
}): Promise<{ id: string; wordCount: number } | null> {
  try {
    const res = await fetch('/api/save-transcription', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...params, id: params.id ?? undefined }),
    })
    const data = (await res.json().catch(() => null)) as { success?: boolean; id?: string; wordCount?: number } | null
    if (!res.ok || !data?.success || !data.id) return null
    return { id: data.id, wordCount: data.wordCount ?? 0 }
  } catch {
    return null
  }
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 o'
  const units = ['o', 'Ko', 'Mo', 'Go']
  const k = 1024
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  const value = bytes / Math.pow(k, i)
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

function isValidFileType(file: File): boolean {
  const ext = '.' + file.name.split('.').pop()?.toLowerCase()
  return ACCEPTED_EXTENSIONS.includes(ext) || ACCEPTED_MIME_TYPES.includes(file.type)
}

function downloadTxt(text: string, filename: string) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length
}

function formatNumber(n: number): string {
  return n.toLocaleString('fr-FR')
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`
  return `${s}s`
}

// ---------------------------------------------------------------------------
// Context file text extraction (client-side)
// ---------------------------------------------------------------------------

function getFileExtension(filename: string): string {
  return ('.' + filename.split('.').pop()?.toLowerCase()) as string
}

function isValidContextFileType(file: File): boolean {
  const ext = getFileExtension(file.name)
  return CONTEXT_ACCEPTED_EXTENSIONS.includes(ext)
}

async function extractTextFromFile(file: File): Promise<string> {
  const ext = getFileExtension(file.name)
  switch (ext) {
    case '.txt':
      return file.text()
    case '.pdf': {
      const pdfjsLib = await import('pdfjs-dist')
      pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url,
      ).toString()
      const buffer = await file.arrayBuffer()
      const pdf = await pdfjsLib.getDocument({ data: buffer }).promise
      const pages: string[] = []
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i)
        const content = await page.getTextContent()
        const pageText = content.items.map((item) => ('str' in item ? item.str : '')).join(' ')
        pages.push(pageText)
      }
      return pages.join('\n')
    }
    case '.docx': {
      const mammoth = await import('mammoth')
      const buffer = await file.arrayBuffer()
      const result = await mammoth.default.extractRawText({ arrayBuffer: buffer })
      return result.value
    }
    case '.xlsx':
    case '.xls': {
      const ExcelJS = (await import('exceljs')).default
      const buffer = await file.arrayBuffer()
      const workbook = new ExcelJS.Workbook()
      await workbook.xlsx.load(buffer)
      const sheets: string[] = []
      workbook.eachSheet((sheet) => {
        const rows: string[] = []
        sheet.eachRow((row) => {
          const cells = (row.values as unknown[]).slice(1).map(v => {
            if (v == null) return ''
            // Cellules avec formule ou rich-text : lire le champ .text
            if (typeof v === 'object' && v !== null && 'text' in v) {
              return String((v as { text: unknown }).text ?? '')
            }
            return String(v)
          })
          rows.push(cells.join(','))
        })
        sheets.push(`[Feuille : ${sheet.name}]\n${rows.join('\n')}`)
      })
      return sheets.join('\n\n')
    }
    default:
      throw new Error(`Format non supporté : ${ext}`)
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ContextFile = {
  id: string
  name: string
  size: number
  text: string
  type: string
}

type TranscriptionMode = 'simple' | 'diarize'

type DiarizedSegment = {
  speaker: string
  start: number
  end: number
  text: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Module01({ compact, onHasResult }: { compact?: boolean; onHasResult?: (has: boolean) => void } = {}) {
  // -- State ----------------------------------------------------------------
  const [step, setStep] = useState(1)
  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [selectedMode, setSelectedMode] = useState<TranscriptionMode>('simple')
  const [isTranscribing, setIsTranscribing] = useState(false)
  // Wake Lock pour empêcher l'écran de s'éteindre pendant la transcription
  // (sinon Windows/macOS suspend le réseau et la connexion XHR/SSE meurt).
  // Le navigateur libère automatiquement le lock quand l'onglet devient hidden,
  // donc le useEffect plus bas le ré-acquiert au retour de visibilité.
  const wakeLockRef = useRef<WakeLockSentinel | null>(null)
  // Annulation de la transcription en cours (bouton « Annuler »). On garde une
  // ref sur la requête XHR active pour pouvoir l'interrompre, et un flag pour
  // que le flux n'enchaîne pas sur le succès (incrément, étape 3, auto-save)
  // après une annulation volontaire.
  const transcribeXhrRef = useRef<XMLHttpRequest | null>(null)
  const transcribeAbortedRef = useRef(false)
  const [uploadProgress, setUploadProgress] = useState<{ pct: number } | null>(null)
  const [transcriptionProgress, setTranscriptionProgress] = useState<{ pct: number; message: string } | null>(null)
  const [isCorrecting, setIsCorrecting] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [rawTranscription, setRawTranscription] = useState('')
  const [correctedText, setCorrectedText] = useState('')
  const [editableText, setEditableText] = useState('')
  const [contexte, setContexte] = useState('')
  const [, setCopiedRaw] = useState(false)
  const [copiedCorrected, setCopiedCorrected] = useState(false)
  const [copiedEditable, setCopiedEditable] = useState(false)
  const [savedTranscriptionId, setSavedTranscriptionId] = useState<string | null>(null)
  const [autoSaved, setAutoSaved] = useState(false)
  const [diarizedSegments, setDiarizedSegments] = useState<DiarizedSegment[]>([])

  // -- Historique récent (48 h) --------------------------------------------
  const [history, setHistory] = useState<RecentTranscription[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)

  // Context files state
  const [contextFiles, setContextFiles] = useState<ContextFile[]>([])
  const [, setIsContextDragging] = useState(false)
  const [isParsingFiles, setIsParsingFiles] = useState(false)
  const [showNoContextDialog, setShowNoContextDialog] = useState(false)
  const [showNoParamsDialog, setShowNoParamsDialog] = useState(false)

  // -- Paramètres de l'entretien -------------------------------------------
  const [interviewParams, setInterviewParams] = useState({
    projet: '',
    repondant: '',
    statut: '',
    localite: '',
    dateEntretien: new Date().toISOString().slice(0, 10),
    duree: '',
    enqueteur: '',
    langue: 'Français',
  })

  const setParam = (key: keyof typeof interviewParams) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setInterviewParams(prev => ({ ...prev, [key]: e.target.value }))
  }

  // Vrai si au moins un champ utile a été renseigné par l'utilisateur.
  // On ignore `dateEntretien` (auto-rempli aujourd'hui) et `langue` (défaut "Français").
  const hasFilledParams = !!(
    interviewParams.projet.trim() ||
    interviewParams.repondant.trim() ||
    interviewParams.statut.trim() ||
    interviewParams.localite.trim() ||
    interviewParams.duree.trim() ||
    interviewParams.enqueteur.trim()
  )

  const { limits, incrementLimit, openUpgrade, openAuthGate, isAuthenticated } = useFreemium()
  // Plan gratuit : blocage si un autre module a été utilisé (verrou) OU si
  // l'heure gratuite (100 crédits) est réellement épuisée.
  // IMPORTANT : ne s'applique qu'aux utilisateurs CONNECTÉS. Un visiteur
  // anonyme (credits=0 par défaut) doit toujours voir le bouton « Lancer »
  // pour déclencher la pop-up d'inscription — sinon il est bloqué à tort.
  //
  // Deux causes distinctes → deux CTA distincts :
  //  - lockedByOther → autre module gratuit déjà utilisé → « Passer en Pro »
  //  - exhausted     → crédits transcription réellement à 0 → « Limite atteinte »
  const transcriptionLockedByOther =
    isAuthenticated && !limits.is_pro &&
    limits.free_module_used !== null && limits.free_module_used !== 'transcription'
  const transcriptionExhausted =
    isAuthenticated && !limits.is_pro && !transcriptionLockedByOther &&
    (limits.credits ?? 0) <= 0
  const transcriptionBlocked = transcriptionLockedByOther || transcriptionExhausted
  // Correction : toujours réservée au plan Pro (jamais une « limite atteinte »).
  const correctionBlocked = !limits.is_pro

  // Lancement en attente : un visiteur non connecté a cliqué sur « Lancer la
  // transcription », la popup d'inscription s'est affichée. Une fois connecté,
  // on relance — le fichier audio déjà chargé reste en mémoire.
  const [pendingAuthLaunch, setPendingAuthLaunch] = useState(false)

  // -- Wake Lock helpers ---------------------------------------------------
  // Empêche l'écran de s'éteindre pendant la transcription (sinon le réseau
  // se suspend et la connexion HTTP meurt). API standard, supportée par
  // Chrome/Edge/Safari ≥ 16.4. Sur Firefox < 126 ou sans permissions :
  // silently noop — la transcription marchera comme avant.
  const acquireWakeLock = useCallback(async () => {
    try {
      if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return
      // Si on en a déjà un actif, on garde celui-là
      if (wakeLockRef.current && !wakeLockRef.current.released) return
      wakeLockRef.current = await navigator.wakeLock.request('screen')
    } catch {
      // Permissions refusées, page hidden, etc. — non-bloquant
    }
  }, [])

  const releaseWakeLock = useCallback(async () => {
    try {
      if (wakeLockRef.current && !wakeLockRef.current.released) {
        await wakeLockRef.current.release()
      }
    } catch {
      // ignore
    } finally {
      wakeLockRef.current = null
    }
  }, [])

  // Le navigateur libère AUTO le wake lock quand la page devient hidden.
  // Quand l'utilisateur revient sur l'onglet et que la transcription est
  // toujours en cours, on ré-acquiert le lock.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isTranscribing) {
        void acquireWakeLock()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [isTranscribing, acquireWakeLock])

  const fileInputRef = useRef<HTMLInputElement>(null)
  // Inputs séparés audio / vidéo — utilisés sur MOBILE uniquement pour proposer
  // deux choix explicites (accept="audio/*" seul / accept="video/*" seul) et
  // clarifier le sélecteur Android. Desktop continue d'utiliser fileInputRef.
  const audioInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)
  const isMobile = useIsMobile()
  const dragCounter = useRef(0)
  const contextFileInputRef = useRef<HTMLInputElement>(null)
  const contextDragCounter = useRef(0)

  // -- Persistance localStorage — clé qualyse_module02_result (Module 02 = Transcription)
  //    Migration silencieuse depuis l'ancienne clé qualyse_m01_verbatim si présente.
  //    Stocke : verbatim brut, verbatim corrigé, version éditable, paramètres entretien.
  const TRANSCRIPTION_LS_KEY = 'qualyse_module02_result'
  const LEGACY_TRANSCRIPTION_LS_KEY = 'qualyse_m01_verbatim'
  const [restoredFromCache, setRestoredFromCache] = useState(false)

  useEffect(() => {
    try {
      let saved = localStorage.getItem(TRANSCRIPTION_LS_KEY)
      let camefromLegacy = false
      if (!saved) {
        saved = localStorage.getItem(LEGACY_TRANSCRIPTION_LS_KEY)
        camefromLegacy = !!saved
      }
      if (!saved) return
      const parsed = JSON.parse(saved) as {
        raw?: string
        editable?: string
        corrected?: string
        params?: Partial<typeof interviewParams>
      }
      if (parsed.raw) { setRawTranscription(parsed.raw); setEditableText(parsed.editable ?? parsed.raw) }
      if (parsed.corrected) setCorrectedText(parsed.corrected)
      if (parsed.params) setInterviewParams(prev => ({ ...prev, ...parsed.params }))
      if (parsed.raw || parsed.corrected) setRestoredFromCache(true)
      if (camefromLegacy) {
        try {
          localStorage.setItem(TRANSCRIPTION_LS_KEY, saved)
          localStorage.removeItem(LEGACY_TRANSCRIPTION_LS_KEY)
        } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    try {
      if (rawTranscription || correctedText) {
        localStorage.setItem(TRANSCRIPTION_LS_KEY, JSON.stringify({
          raw: rawTranscription,
          editable: editableText,
          corrected: correctedText,
          params: {
            repondant: interviewParams.repondant,
            dateEntretien: interviewParams.dateEntretien,
            localite: interviewParams.localite,
            projet: interviewParams.projet,
          },
        }))
      }
    } catch { /* ignore */ }
  }, [rawTranscription, editableText, correctedText, interviewParams])

  const dismissRestoredBanner = useCallback(() => {
    setRestoredFromCache(false)
    setRawTranscription('')
    setCorrectedText('')
    setEditableText('')
    try { localStorage.removeItem(TRANSCRIPTION_LS_KEY); localStorage.removeItem(LEGACY_TRANSCRIPTION_LS_KEY) } catch { /* ignore */ }
    toast.info('Résultat précédent effacé.')
  }, [])

  // Derived states
  const hasRaw = rawTranscription.trim().length > 0
  const hasCorrected = correctedText.trim().length > 0
  const baseName = file?.name.replace(/\.[^/.]+$/, '') ?? 'verbatim'

  useEffect(() => { onHasResult?.(hasRaw) }, [hasRaw, onHasResult])

  // Combined context
  const allContextText = useMemo(() => {
    const parts: string[] = []
    if (contexte.trim()) parts.push(contexte.trim())
    contextFiles.forEach((f) => {
      if (f.text.trim()) parts.push(f.text.trim())
    })
    return parts.join('\n\n---\n\n')
  }, [contexte, contextFiles])

  const hasAnyContext = allContextText.trim().length > 0

  // -- Audio file handling ---------------------------------------------------
  const processFile = useCallback((selected: File) => {
    if (!isValidFileType(selected)) {
      toast.error('Format non supporté', { description: `Formats acceptés : ${ACCEPTED_EXTENSIONS.join(', ')}` })
      return
    }
    const sizeMB = selected.size / (1024 * 1024)
    if (sizeMB > MAX_FILE_SIZE_MB) {
      toast.error('Fichier trop volumineux', {
        description: `Limite : ${MAX_FILE_SIZE_MB} Mo (2 Go). Pour les fichiers plus lourds, découpez l'audio avec Audacity (gratuit).`,
        duration: 10000,
      })
      return
    }
    setFile(selected)
    toast.success('Fichier chargé', { description: `${selected.name} (${formatFileSize(selected.size)})` })
  }, [])

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0]
      if (selected) processFile(selected)
    },
    [processFile],
  )

  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation()
    dragCounter.current += 1
    if (dragCounter.current === 1) setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation()
    dragCounter.current -= 1
    if (dragCounter.current === 0) setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault(); e.stopPropagation()
      dragCounter.current = 0
      setIsDragging(false)
      const dropped = e.dataTransfer.files?.[0]
      if (dropped) processFile(dropped)
    },
    [processFile],
  )

  // -- Context file handling ------------------------------------------------
  const processContextFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files)
    const remaining = MAX_CONTEXT_FILES - contextFiles.length
    if (remaining <= 0) {
      toast.error('Limite atteinte', { description: `Maximum ${MAX_CONTEXT_FILES} documents de contexte.` })
      return
    }
    const toProcess = fileArray.slice(0, remaining)
    const skipped = fileArray.length - toProcess.length
    if (skipped > 0) {
      toast.warning(`${skipped} fichier(s) ignoré(s)`, { description: `Limite de ${MAX_CONTEXT_FILES} documents atteinte.` })
    }
    setIsParsingFiles(true)
    const newFiles: ContextFile[] = []
    for (const f of toProcess) {
      if (!isValidContextFileType(f)) {
        toast.error('Format non supporté', { description: `Fichier « ${f.name} » : formats acceptés PDF, DOCX, XLSX, TXT.` })
        continue
      }
      const sizeMB = f.size / (1024 * 1024)
      if (sizeMB > MAX_CONTEXT_FILE_SIZE_MB) {
        toast.error('Fichier trop volumineux', {
          description: `« ${f.name} » (${formatFileSize(f.size)}) dépasse la limite de ${MAX_CONTEXT_FILE_SIZE_MB} Mo.`,
        })
        continue
      }
      try {
        const text = await extractTextFromFile(f)
        const ext = getFileExtension(f.name)
        newFiles.push({ id: crypto.randomUUID(), name: f.name, size: f.size, text, type: ext })
      } catch (error) {
        console.error('[contexte] extraction échouée:', f.name, error)
        toast.error('Erreur de lecture', {
          description: `Impossible d'extraire le texte de « ${f.name} ». Vérifiez qu'il n'est pas protégé ou endommagé.`,
        })
      }
    }
    if (newFiles.length > 0) {
      setContextFiles((prev) => [...prev, ...newFiles])
      toast.success(`${newFiles.length} document(s) chargé(s)`, {
        description: 'Le contenu textuel a été extrait et sera utilisé comme contexte.',
      })
    }
    setIsParsingFiles(false)
  }, [contextFiles.length])

  const removeContextFile = useCallback((id: string) => {
    setContextFiles((prev) => prev.filter((f) => f.id !== id))
    toast.info('Document retiré')
  }, [])

  const handleContextFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (files && files.length > 0) processContextFiles(files)
      if (contextFileInputRef.current) contextFileInputRef.current.value = ''
    },
    [processContextFiles],
  )

  const handleContextDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation()
    contextDragCounter.current += 1
    if (contextDragCounter.current === 1) setIsContextDragging(true)
  }, [])

  const handleContextDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation()
    contextDragCounter.current -= 1
    if (contextDragCounter.current === 0) setIsContextDragging(false)
  }, [])

  const handleContextDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation()
  }, [])

  const handleContextDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault(); e.stopPropagation()
      contextDragCounter.current = 0
      setIsContextDragging(false)
      if (e.dataTransfer.files.length > 0) processContextFiles(e.dataTransfer.files)
    },
    [processContextFiles],
  )

  // -- Transcription (single chunk via XHR — real upload progress) -----------
  const transcribeChunk = useCallback((
    chunk: File,
    mode: string,
    onUploadProgress?: (pct: number) => void,
    durationSec?: number,
  ): Promise<{ text: string; diarizedSegments?: DiarizedSegment[]; duration: string }> => {
    return new Promise((resolve, reject) => {
      const formData = new FormData()
      formData.append('audio', chunk)
      formData.append('mode', mode)
      // Durée détectée côté client — nécessaire pour le calcul exact du coût
      // crédits (plan gratuit : 1.667 crédit/min ; plan Pro : 0.57 crédit/min).
      if (durationSec && durationSec > 0) formData.append('duration', String(durationSec))
      const xhr = new XMLHttpRequest()
      transcribeXhrRef.current = xhr
      xhr.open('POST', '/api/transcribe')
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onUploadProgress) {
          onUploadProgress(Math.round((e.loaded / e.total) * 100))
        }
      }
      // Annulation utilisateur → rejet neutre (intercepté plus haut).
      xhr.onabort = () => reject(new DOMException('Annulé', 'AbortError'))
      xhr.onload = () => {
        if (xhr.status < 200 || xhr.status >= 300) {
          try { reject(new Error(JSON.parse(xhr.responseText).error || `Erreur serveur (${xhr.status})`)) }
          catch { reject(new Error(`Erreur serveur (${xhr.status})`)) }
          return
        }
        try {
          const data = JSON.parse(xhr.responseText) as {
            success: boolean; text: string; diarizedSegments?: DiarizedSegment[]; duration: string
          }
          if (!data.success) reject(new Error(data.text || 'Erreur inconnue'))
          else resolve(data)
        } catch { reject(new Error('Réponse invalide du serveur')) }
      }
      // `onerror` implique un statut 0 : la requête n'a pas abouti. C'est le
      // SEUL cas réellement « réseau ». On ne fabrique plus la chaîne « Erreur
      // réseau » que `friendlyError` reconnaissait par sous-chaîne : la cause
      // est portée par le type de l'erreur.
      xhr.onerror = () => reject(new UploadFailureError({ ok: false, kind: 'network', status: 0, detail: '' }))
      xhr.ontimeout = () => reject(new Error('La requête a pris trop de temps. Vérifiez votre connexion et réessayez.'))
      xhr.timeout = 5 * 60 * 1000
      xhr.send(formData)
    })
  }, [])

  // -- Transcription --------------------------------------------------------
  const handleTranscribe = useCallback(async () => {
    if (!file) { toast.error('Aucun fichier sélectionné'); return }
    // Inscription obligatoire avant tout traitement : on affiche la popup et on
    // garde le lancement en attente — le fichier audio déjà chargé reste intact.
    if (!isAuthenticated) {
      setPendingAuthLaunch(true)
      openAuthGate()
      return
    }
    if (transcriptionBlocked) { openUpgrade(); return }

    // Détection unique de la durée — réutilisée pour le pré-check des crédits
    // en plan gratuit ET passée au serveur pour le calcul exact du coût.
    let detectedDurationSec = 0
    try {
      detectedDurationSec = await new Promise<number>((resolve, reject) => {
        const audio = new Audio()
        const url = URL.createObjectURL(file)
        audio.src = url
        audio.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(audio.duration) }
        audio.onerror = () => { URL.revokeObjectURL(url); reject(new Error('unreadable')) }
      })
    } catch { /* durée indétectable — on laisse le serveur arbitrer */ }

    // Plan gratuit : refuser un fichier dont la durée excède le solde restant.
    // 1.667 crédit/min → minutes restantes = credits / 1.667.
    if (!limits.is_pro && detectedDurationSec > 0) {
      const minutesPerCredit = 60 / 100 // 0.6 min par crédit
      const minutesRemaining = (limits.credits || 0) * minutesPerCredit
      const minutesNeeded = detectedDurationSec / 60
      if (minutesNeeded > minutesRemaining + 0.5) { // tolérance 30s
        const remainingLabel = Math.floor(minutesRemaining)
        toast.error(
          remainingLabel <= 0
            ? 'Heure gratuite épuisée'
            : `Fichier trop long pour vos crédits restants (≈ ${remainingLabel} min disponibles)`,
          { description: 'Passez au plan Pro pour transcrire plus.' },
        )
        openUpgrade()
        return
      }
    }

    transcribeAbortedRef.current = false
    setIsTranscribing(true)
    setDiarizedSegments([])
    setUploadProgress({ pct: 0 })
    setTranscriptionProgress(null)
    setRestoredFromCache(false)
    // Nouvelle transcription → on repart sur une ligne neuve (pas d'update de
    // la précédente) et on remet l'indicateur d'auto-sauvegarde à zéro.
    setSavedTranscriptionId(null)
    setAutoSaved(false)
    // Bascule vers le découpage si le fichier dépasse SOIT la taille SOIT la
    // durée seuil. La durée prime sur la taille : un audio de 40 min à faible
    // débit (< 10 MB) doit être découpé pour ne pas être tronqué par l'API.
    // Si la durée n'a pas pu être détectée (detectedDurationSec === 0), on
    // retombe sur le seul critère de taille (comportement historique).
    const exceedsDuration = detectedDurationSec > CHUNK_DURATION_SEC
    const needsChunking = file.size > CHUNK_SIZE_BYTES || exceedsDuration

    // Acquiert le wake lock — empêche l'écran de s'éteindre et donc le réseau
    // d'être suspendu. Non-bloquant : si l'API n'est pas dispo, on continue.
    await acquireWakeLock()

    // Timer d'animation de la phase « serveur transcrit » (petit fichier).
    let processingTimer: ReturnType<typeof setInterval> | null = null

    // Cause de l'échec de l'envoi direct vers le stockage, conservée jusqu'au
    // `catch` final. Le repli multipart est laissé en place (lot 3 reporté) :
    // s'il aboutit, cette cause n'est jamais affichée ; s'il échoue à son tour,
    // c'est elle la cause racine, pas l'erreur du repli.
    let uploadFailure: UploadFailure | null = null

    try {
      if (!needsChunking) {
        // ── Small file — XHR with real upload progress ────────────────
        let uploadDone = false
        const data = await transcribeChunk(
          new File([file], file.name, { type: file.type }),
          selectedMode,
          (pct) => {
            if (uploadDone) return
            setUploadProgress({ pct })
            if (pct >= 100) {
              // Upload terminé → le serveur transcrit (pas de % renvoyé par
              // l'API). On bascule sur une progression ANIMÉE plafonnée à 92 %
              // jusqu'à la vraie fin, au lieu d'une barre figée.
              uploadDone = true
              setUploadProgress(null)
              let p = 35
              setTranscriptionProgress({ pct: p, message: 'Transcription en cours…' })
              processingTimer = setInterval(() => {
                p = Math.min(p + 2, 92)
                setTranscriptionProgress({ pct: p, message: 'Transcription en cours…' })
              }, 900)
            }
          },
          detectedDurationSec,
        )
        uploadDone = true
        if (processingTimer) { clearInterval(processingTimer); processingTimer = null }
        setUploadProgress(null)
        setTranscriptionProgress({ pct: 100, message: '✅ Transcription terminée !' })
        await new Promise(r => setTimeout(r, 600))
        setRawTranscription(data.text)
        setEditableText(data.text)
        if (data.diarizedSegments) setDiarizedSegments(data.diarizedSegments)
        await incrementLimit('transcription_count')
        setStep(3)
        toast.success('Transcription terminée !', { description: `Durée du fichier : ${data.duration}.`, duration: 8000 })
        // Sauvegarde automatique silencieuse — protège contre la perte (clic
        // accidentel sur « Nouveau projet »). Non bloquant : échec ignoré.
        void persistTranscription({
          fileName: file.name, fileSize: file.size, mode: selectedMode,
          texte: data.text, statut: 'brouillon',
        }).then((saved) => { if (saved) { setSavedTranscriptionId(saved.id); setAutoSaved(true) } })
      } else {
        // ── Large file ────────────────────────────────────────────────
        if (selectedMode === 'diarize') {
          toast.info('Mode "Identifier les locuteurs" non disponible pour les fichiers volumineux — transcription simple appliquée.')
        }

        let storagePath: string | null = null
        const urlRes = await fetch('/api/upload-url', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: file.name }),
        })

        if (!urlRes.ok) {
          // Le 503 « Storage non configuré » et le 401 étaient jetés aussi
          // silencieusement que l'échec du PUT lui-même. On conserve la raison :
          // c'est elle qui explique pourquoi l'envoi direct n'a jamais eu lieu.
          const body = await urlRes.text().catch(() => '')
          uploadFailure = {
            ok: false, kind: 'http', status: urlRes.status, detail: body.slice(0, 500),
          }
          console.error('[upload] /api/upload-url a échoué', {
            status: urlRes.status, body: uploadFailure.detail,
          })
          setUploadProgress(null)
          setTranscriptionProgress({ pct: 0, message: 'Envoi direct en cours...' })
        } else {
          // ── Mode B : envoi direct vers le stockage objet (R2 en prod) ──
          const { signedUrl, path: filePath } = await urlRes.json() as { signedUrl: string; path: string; sessionId: string }
          const outcome = await new Promise<ReturnType<typeof classifyUploadXhr>>((resolve) => {
            const xhr = new XMLHttpRequest()
            transcribeXhrRef.current = xhr
            xhr.open('PUT', signedUrl)
            xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
            xhr.timeout = 60 * 60 * 1000
            xhr.upload.onprogress = (e) => {
              if (e.lengthComputable) setUploadProgress({ pct: Math.round((e.loaded / e.total) * 100) })
            }
            xhr.upload.onload = () => {
              setUploadProgress(null)
              setTranscriptionProgress({ pct: 0, message: 'Fichier stocké, découpage en cours...' })
            }
            // Le statut et le corps ne sont disponibles qu'ici. R2 répond en XML
            // S3 (`<Error><Code>…</Code></Error>`) : c'est l'information la plus
            // utile de toute la chaîne, et elle était détruite sur place.
            xhr.onload = () => resolve(classifyUploadXhr('load', xhr.status, xhr.responseText ?? ''))
            xhr.onerror = () => resolve(classifyUploadXhr('error', xhr.status, ''))
            xhr.ontimeout = () => resolve(classifyUploadXhr('timeout', 0, ''))
            xhr.onabort = () => resolve(classifyUploadXhr('abort', 0, ''))
            xhr.send(file)
          })
          // Annulation pendant l'upload → on sort proprement (pas de fallback).
          if (transcribeAbortedRef.current) throw new DOMException('Annulé', 'AbortError')
          if (outcome.ok) {
            storagePath = filePath
          } else {
            uploadFailure = outcome
            console.error('[upload] envoi direct vers le stockage échoué', {
              kind: outcome.kind,
              status: outcome.status,
              detail: outcome.detail,
              fileSize: file.size,
              fileType: file.type,
            })
            // Le repli multipart reste en place (lot 3 reporté).
            setUploadProgress(null)
            setTranscriptionProgress({ pct: 0, message: 'Envoi direct en cours...' })
          }
        }

        // ── Start transcription SSE (Mode B: storagePath, Mode A: multipart) ──
        const finalText = await new Promise<string>((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          transcribeXhrRef.current = xhr
          xhr.open('POST', '/api/transcribe/large')
          xhr.timeout = 90 * 60 * 1000
          xhr.onabort = () => reject(new DOMException('Annulé', 'AbortError'))
          let sseBuffer = ''
          let lastLength = 0
          let result = ''

          if (!storagePath) {
            xhr.upload.onprogress = (e) => {
              if (e.lengthComputable) setUploadProgress({ pct: Math.round((e.loaded / e.total) * 100) })
            }
            xhr.upload.onload = () => {
              setUploadProgress(null)
              setTranscriptionProgress({ pct: 0, message: 'Fichier reçu, découpage en cours...' })
            }
          }

          const processSseBuffer = () => {
            const lines = sseBuffer.split('\n')
            sseBuffer = lines.pop() ?? ''
            for (const line of lines) {
              if (!line.startsWith('data: ')) continue
              let event: Record<string, unknown>
              try { event = JSON.parse(line.slice(6)) } catch { continue }
              if (event.type === 'heartbeat') {
                // keep-alive — rien à faire
              } else if (event.type === 'status') {
                setTranscriptionProgress({ pct: 5, message: String(event.message) })
              } else if (event.type === 'start') {
                const total = event.total as number
                setTranscriptionProgress({ pct: 10, message: `Audio découpé en ${total} segment${total > 1 ? 's' : ''} de 10 min` })
              } else if (event.type === 'progress') {
                const current = event.current as number
                const total = event.total as number
                const label = event.label ? String(event.label) : `Segment ${current}/${total}`
                const pct = Math.round(10 + (current / total) * 85)
                setTranscriptionProgress({ pct, message: `⏳ ${label}` })
              } else if (event.type === 'segment_done') {
                const current = event.current as number
                const total = event.total as number
                const label = event.label ? String(event.label) : `Segment ${current}/${total}`
                const pct = Math.round(10 + (current / total) * 85)
                setTranscriptionProgress({ pct, message: `${label}` })
              } else if (event.type === 'segment_failed') {
                const current = event.current as number
                const total = event.total as number
                const label = event.label ? String(event.label) : `Segment ${current}/${total}`
                const pct = Math.round(10 + (current / total) * 85)
                setTranscriptionProgress({ pct, message: `⚠️ ${label} — poursuite...` })
              } else if (event.type === 'done') {
                result = String(event.text)
                const stats = event.stats as { segments?: number; failed?: number; totalWords?: number } | undefined
                const failMsg = stats?.failed ? ` (${stats.failed} segment(s) manquant(s))` : ''
                setTranscriptionProgress({ pct: 100, message: `✅ Terminé — ${stats?.totalWords ?? '?'} mots${failMsg}` })
              } else if (event.type === 'error') {
                reject(new Error(String(event.message)))
              }
            }
          }

          xhr.onprogress = () => {
            const newChunk = xhr.responseText.slice(lastLength)
            lastLength = xhr.responseText.length
            sseBuffer += newChunk
            processSseBuffer()
          }

          xhr.onload = () => {
            // Flush any remaining SSE data that onprogress might not have seen.
            // The XHR spec allows the browser to skip the final onprogress event
            // if the last chunk arrives simultaneously with connection close.
            const remaining = xhr.responseText.slice(lastLength)
            if (remaining) { sseBuffer += remaining; processSseBuffer() }
            if (xhr.status >= 200 && xhr.status < 300) resolve(result)
            else {
              try { reject(new Error(JSON.parse(xhr.responseText).error || `Erreur serveur (${xhr.status})`)) }
              catch { reject(new Error(`Erreur serveur (${xhr.status})`)) }
            }
          }
          // `onerror` implique un statut 0 : la requête n'a pas abouti. C'est le
          // SEUL cas réellement « réseau ». On ne fabrique plus la chaîne
          // « Erreur réseau » que `friendlyError` reconnaissait par sous-chaîne :
          // la cause est portée par le type de l'erreur.
          xhr.onerror = () => reject(new UploadFailureError({ ok: false, kind: 'network', status: 0, detail: '' }))
          xhr.ontimeout = () => reject(new Error(
            'La transcription a dépassé 90 minutes. Vérifiez votre connexion ou découpez le fichier en parties de 1h.'
          ))

          if (storagePath) {
            xhr.setRequestHeader('Content-Type', 'application/json')
            xhr.send(JSON.stringify({
              storagePath,
              filename: file.name,
              // Durée client → pré-check précis du solde côté serveur.
              durationSec: detectedDurationSec || undefined,
            }))
          } else {
            // Mode A — send file directly (fallback)
            const formData = new FormData()
            formData.append('audio', file)
            formData.append('mode', 'simple')
            if (detectedDurationSec > 0) formData.append('duration', String(detectedDurationSec))
            xhr.send(formData)
          }
        })

        if (transcribeAbortedRef.current) return
        await new Promise(r => setTimeout(r, 700))
        setRawTranscription(finalText)
        setEditableText(finalText)
        await incrementLimit('transcription_count')
        setStep(3)
        toast.success('Transcription terminée !', {
          description: 'Tous les segments ont été fusionnés avec succès.',
          duration: 8000,
        })
        // Sauvegarde automatique silencieuse (cf. chemin petit fichier).
        void persistTranscription({
          fileName: file.name, fileSize: file.size, mode: selectedMode,
          texte: finalText, statut: 'brouillon',
        }).then((saved) => { if (saved) { setSavedTranscriptionId(saved.id); setAutoSaved(true) } })
      }
    } catch (error) {
      // Annulation volontaire → message neutre, pas une erreur. Le succès
      // (incrément, étape 3, auto-save) n'a pas été atteint → rien à nettoyer.
      if (transcribeAbortedRef.current || (error instanceof Error && error.name === 'AbortError')) {
        toast.info('Transcription annulée.')
      } else if (uploadFailure && uploadFailure.kind === 'http') {
        // L'envoi direct a été REFUSÉ par le stockage (statut réel, cause
        // déterministe). Le repli multipart a échoué ensuite, mais il ne
        // pouvait pas aboutir : c'est le refus initial qu'il faut montrer.
        // Un échec réseau ou un délai dépassé, eux, n'invalident pas le repli —
        // dans ces cas c'est bien son erreur à lui qui est la plus pertinente.
        toast.error('Échec de la transcription', {
          description: uploadFailureMessage(uploadFailure),
        })
      } else {
        toast.error('Échec de la transcription', { description: friendlyError(error, 'processing') })
      }
    } finally {
      if (processingTimer) clearInterval(processingTimer)
      setIsTranscribing(false)
      setUploadProgress(null)
      setTranscriptionProgress(null)
      transcribeXhrRef.current = null
      void releaseWakeLock()
    }
  }, [file, selectedMode, transcribeChunk, transcriptionBlocked, limits.is_pro, openUpgrade, incrementLimit, acquireWakeLock, releaseWakeLock, isAuthenticated, openAuthGate])

  // Annule la transcription en cours (interrompt la requête XHR active).
  const handleCancelTranscribe = useCallback(() => {
    transcribeAbortedRef.current = true
    transcribeXhrRef.current?.abort()
  }, [])

  // Reprise automatique : dès que l'utilisateur est connecté, on relance la
  // transcription qui était en attente, sans qu'il ait à recliquer.
  // Après une connexion Google (popup), la session vient d'être posée : on
  // laisse un court délai pour qu'elle soit pleinement propagée côté serveur
  // (cookies) avant l'appel API, sinon la transcription peut partir avec une
  // session encore instable → 401 « session expirée ».
  useEffect(() => {
    if (pendingAuthLaunch && isAuthenticated) {
      setPendingAuthLaunch(false)
      setTimeout(() => { void handleTranscribe() }, AUTH_RESUME_SETTLE_MS)
    }
  }, [pendingAuthLaunch, isAuthenticated, handleTranscribe])

  // -- Correction IA --------------------------------------------------------
  const executeCorrection = useCallback(async () => {
    if (!rawTranscription.trim()) { toast.error('Aucun verbatim à corriger'); return }
    setIsCorrecting(true)
    setCorrectedText('')
    try {
      let contexteBlock = contexte.trim()
      if (contextFiles.length > 0) {
        const docTexts = contextFiles
          .filter((f) => f.text.trim())
          .map((f) => `--- Document : ${f.name} ---\n${f.text.trim()}`)
        if (docTexts.length > 0) {
          const docsBlock = docTexts.join('\n\n')
          contexteBlock = contexteBlock
            ? `${contexteBlock}\n\n--- Documents joints ---\n${docsBlock}`
            : `--- Documents joints ---\n${docsBlock}`
        }
      }
      const body: { verbatim: string; langueCible?: string; contexteRecherche?: string } = {
        verbatim: rawTranscription,
        langueCible: 'français',
      }
      if (contexteBlock) body.contexteRecherche = contexteBlock
      const response = await fetch('/api/correct', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!response.ok || !response.body) {
        const errData = await response.json().catch(() => ({}))
        throw new Error((errData as Record<string, string>).error || `Erreur serveur (${response.status})`)
      }
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let accumulated = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          let event: Record<string, unknown>
          try { event = JSON.parse(line.slice(6)) } catch { continue }
          if (event.type === 'delta') {
            accumulated += String(event.text)
            setCorrectedText(accumulated)
          } else if (event.type === 'done') {
            await incrementLimit('correction_count')
            toast.success('Correction terminée', { description: 'Le verbatim corrigé est prêt ci-dessous.' })
          } else if (event.type === 'error') {
            throw new Error(String(event.message))
          }
        }
      }
      if (!accumulated.trim()) throw new Error('La correction a retourné un résultat vide.')
    } catch (error) {
      toast.error('Échec de la correction', { description: friendlyError(error, 'processing') })
    } finally {
      setIsCorrecting(false)
    }
  }, [rawTranscription, contexte, contextFiles, incrementLimit])

  const handleCorrect = useCallback(() => {
    if (!rawTranscription.trim()) { toast.error('Aucun verbatim à corriger'); return }
    if (correctionBlocked) { openUpgrade(); return }
    if (!hasAnyContext) { setShowNoContextDialog(true); return }
    executeCorrection()
  }, [rawTranscription, hasAnyContext, executeCorrection, correctionBlocked, openUpgrade])

  const handleProceedWithoutContext = useCallback(() => {
    setShowNoContextDialog(false)
    executeCorrection()
  }, [executeCorrection])

  // -- Historique récent (48 h) --------------------------------------------
  // Lecture filtrée par user_id (la route /api/transcriptions ne renvoie que
  // les lignes de l'utilisateur connecté) + filtre created_at < 48 h côté client.
  const fetchHistory = useCallback(async () => {
    if (!isAuthenticated) { setHistory([]); return }
    setLoadingHistory(true)
    try {
      const res = await fetch('/api/transcriptions')
      if (!res.ok) { setHistory([]); return }
      const data = (await res.json()) as { transcriptions?: RecentTranscription[] }
      const cutoff = Date.now() - HISTORY_WINDOW_MS
      const recent = (data.transcriptions ?? []).filter(
        (t) => new Date(t.created_at).getTime() >= cutoff,
      )
      setHistory(recent)
    } catch {
      setHistory([])
    } finally {
      setLoadingHistory(false)
    }
  }, [isAuthenticated])

  // Recharge à l'ouverture, et après chaque sauvegarde (savedTranscriptionId
  // change → la nouvelle entrée auto-sauvegardée apparaît dans l'historique).
  useEffect(() => { void fetchHistory() }, [fetchHistory, savedTranscriptionId])

  // Re-télécharge le Word d'une entrée d'historique (régénéré depuis le texte
  // stocké — déterministe, même logique que le téléchargement courant).
  const handleDownloadHistory = useCallback(async (entry: RecentTranscription) => {
    if (!entry.texte?.trim()) { toast.error('Texte indisponible pour cette entrée.'); return }
    const base = (entry.file_name || 'transcription').replace(/\.[^/.]+$/, '')
    try {
      await exportToWord({
        text: entry.texte,
        title: `Transcription audio — ${base}`,
        documentType: 'Transcription audio',
        sourceFile: entry.file_name ?? base,
        wordCount: entry.word_count ?? countWords(entry.texte),
        filename: `${base}_transcription.docx`,
      })
      toast.success('Document Word téléchargé')
    } catch (err) {
      toast.error(friendlyError(err, 'export'))
    }
  }, [])

  // -- Save to Prisma -------------------------------------------------------
  const handleSave = useCallback(async () => {
    if (!editableText.trim()) { toast.error('Aucun texte à sauvegarder'); return }
    if (!file) return
    setIsSaving(true)
    try {
      const saved = await persistTranscription({
        id: savedTranscriptionId, // met à jour la ligne auto-sauvegardée (pas de doublon)
        fileName: file.name,
        fileSize: file.size,
        mode: selectedMode,
        texte: editableText,
        statut: hasCorrected ? 'corrigé' : 'brouillon',
      })
      if (!saved) throw new Error('Échec de l\'enregistrement.')
      setSavedTranscriptionId(saved.id)
      setAutoSaved(true)
      void fetchHistory()
      toast.success('Transcription sauvegardée', {
        description: `${formatNumber(saved.wordCount)} mots enregistrés en base de données.`,
        duration: 5000,
      })
    } catch (error) {
      toast.error('Échec de la sauvegarde', { description: friendlyError(error, 'generic') })
    } finally {
      setIsSaving(false)
    }
  }, [file, editableText, selectedMode, hasCorrected, savedTranscriptionId, fetchHistory])

  // -- Send to Codification -------------------------------------------------
  const handleSendToCodification = useCallback(() => {
    if (!editableText.trim()) { toast.error('Aucun texte à envoyer'); return }
    toast.info('Fonctionnalité en cours de développement', {
      description: 'L\'envoi vers la Codification thématique sera bientôt disponible.',
      duration: 5000,
    })
  }, [editableText])

  // -- Copy / Download helpers ----------------------------------------------
  const handleCopy = useCallback(async (text: string, setter: (v: boolean) => void, label: string) => {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setter(true)
      toast.success(`${label} copié`)
      setTimeout(() => setter(false), 2000)
    } catch { toast.error('Impossible de copier') }
  }, [])

  const handleDownloadTxt = useCallback((text: string, suffix: string) => {
    downloadTxt(text, `${baseName}_${suffix}.txt`)
    toast.success('Téléchargement lancé')
  }, [baseName])

  const handleDownloadWord = useCallback(async (text: string, suffix: string) => {
    const isRaw = suffix === 'transcription'
    try {
      await exportToWord({
        text,
        title: isRaw ? `Transcription audio — ${baseName}` : `Verbatim corrigé — ${baseName}`,
        documentType: isRaw ? 'Transcription audio' : 'Verbatim corrigé',
        sourceFile: file?.name ?? baseName,
        wordCount: countWords(text),
        interviewParams,
        filename: `${baseName}_${suffix}.docx`,
      })
      toast.success('Document Word téléchargé')
    } catch (err) {
      toast.error(friendlyError(err, 'export'))
    }
  }, [baseName, file, interviewParams])

  // -- Reset ----------------------------------------------------------------
  const handleReset = useCallback(() => {
    setStep(1)
    setFile(null)
    setRawTranscription('')
    setCorrectedText('')
    setEditableText('')
    setContexte('')
    setSelectedMode('simple')
    setCopiedRaw(false)
    setCopiedCorrected(false)
    setCopiedEditable(false)
    setIsTranscribing(false)
    void releaseWakeLock()
    setIsCorrecting(false)
    setIsSaving(false)
    setSavedTranscriptionId(null)
    setDiarizedSegments([])
    setContextFiles([])
    setIsParsingFiles(false)
    setShowNoContextDialog(false)
    setRestoredFromCache(false)
    try { localStorage.removeItem(TRANSCRIPTION_LS_KEY); localStorage.removeItem(LEGACY_TRANSCRIPTION_LS_KEY) } catch { /* ignore */ }
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (contextFileInputRef.current) contextFileInputRef.current.value = ''
    toast.info('Module réinitialisé')
  }, [releaseWakeLock])

  // =========================================================================
  // RENDER
  // =========================================================================
  return (
    <TooltipProvider delayDuration={300}>
      <div style={compact
        ? { display: 'flex', flexDirection: 'column', gap: 20, width: '100%' }
        : { maxWidth: 896, margin: '0 auto', width: '100%' }
      }>
        {/* ── Top section : descriptif + stepper en 2 colonnes ───────────── */}
        <div className="qly-m01-top" style={compact
          ? { display: 'grid', gridTemplateColumns: '280px 1fr', gap: 24, alignItems: 'stretch' }
          : {}
        }>

        {/* ── Bloc descriptif ────────────────────────────────────────────── */}
        <div className="qly-m01-descriptif" style={compact
          ? { padding: 28, background: '#e8f0f8', border: '1px solid #b5d4f4', borderRadius: 12, display: 'flex', flexDirection: 'column' }
          : { marginBottom: 20, padding: 28, borderRadius: 12, border: '1px solid #b5d4f4', background: '#e8f0f8' }
        }>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 46, height: 46, borderRadius: 10, background: 'white', border: '1px solid #b5d4f4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Mic size={22} color={M_COLOR} />
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, fontFamily: 'monospace', letterSpacing: '0.08em', color: M_COLOR }}>MODULE 02</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#111', marginTop: 2, letterSpacing: '-0.3px' }}>Transcription & Correction des verbatims</div>
            </div>
          </div>
          <p style={{ fontSize: 13, color: '#2c2c2c', lineHeight: 1.75, margin: 0 }}>
            Déposez vos fichiers audio ou vidéo d&apos;entretiens. Qualyse transcrit automatiquement dans 98 langues. Il corrige ensuite le verbatim brut (orthographe, ponctuation, structure des tours de parole) en préservant les hésitations analytiquement significatives.
          </p>
        </div>

        {/* ── Stepper + contenu (carte blanche séparée en mode compact) ──── */}
        <div className="qly-m01-stepper-wrap" style={compact
          ? { background: '#fff', border: '1px solid #b5d4f4', borderRadius: 12, overflow: 'hidden', padding: '24px 28px' }
          : {}
        }>

        {/* ── Stepper ────────────────────────────────────────────────────── */}
        <div className="qly-stepper" style={{ display: 'flex', gap: 6, marginBottom: 20, alignItems: 'stretch' }}>
          {M_STEPS.map((s, i) => {
            const n = i + 1
            const isActive = step === n
            const isDone = step > n
            return (
              <div key={n} className="qly-step-item" style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                {i > 0 && (
                  <span className="qly-step-sep" style={{ padding: '0 3px', fontSize: 24, color: '#BBBBBB', fontWeight: 900, flexShrink: 0, userSelect: 'none', lineHeight: 1 }}>›</span>
                )}
                <div
                  onClick={() => isDone && setStep(n)}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', gap: 10,
                    padding: '12px 14px', borderRadius: 10, minWidth: 0,
                    border: isActive ? `2px solid ${M_COLOR}` : isDone ? `1.5px solid ${M_COLOR}55` : '1.5px solid #DEDEDE',
                    background: isActive ? M_BG : isDone ? `${M_COLOR}08` : 'white',
                    boxShadow: isActive ? `0 4px 14px ${M_COLOR}35` : 'none',
                    cursor: isDone ? 'pointer' : 'default', transition: 'all 0.2s',
                  }}
                >
                  <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, background: (isActive || isDone) ? M_COLOR : 'white', border: (isActive || isDone) ? 'none' : '1.5px solid #C0C0C0', color: (isActive || isDone) ? 'white' : '#666' }}>
                    {isDone ? '✓' : n}
                  </div>
                  <div style={{ minWidth: 0, overflow: 'hidden' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: (isActive || isDone) ? M_COLOR : '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</div>
                    <div className="qly-step-hint" style={{ fontSize: 10, color: '#6B7280', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.hint}</div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* ── Step card ──────────────────────────────────────────────────── */}
        <div className="qly-module-card" style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden' }}>
          <div className="qly-step-content" style={{ padding: '24px 28px' }}>

            {/* ── STEP 1 : Upload audio ─────────────────────────────────── */}
            {step === 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                {/* Drop zone */}
                <div
                  role="button" tabIndex={0}
                  aria-label="Zone de dépôt de fichier audio ou vidéo"
                  onClick={() => { if (!isMobile) fileInputRef.current?.click() }}
                  onKeyDown={(e) => { if (!isMobile && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); fileInputRef.current?.click() } }}
                  onDragEnter={handleDragEnter} onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver} onDrop={handleDrop}
                  style={{
                    minHeight: 140, cursor: 'pointer', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 12, borderRadius: 10,
                    border: isDragging ? `2px dashed ${M_COLOR}` : file ? `2px dashed ${M_COLOR}60` : '2px dashed #D1D5DB',
                    background: isDragging ? M_BG : file ? `${M_COLOR}08` : '#F9FAFB', transition: 'all 0.2s',
                  }}
                >
                  {/* accept en types MIME génériques (pas une liste d'extensions) :
                      Android résout mieux audio/* + video/* et expose le sélecteur
                      de Fichiers (notes vocales WhatsApp, etc.), au lieu de ne
                      proposer que caméra / enregistreur / galerie. N'affecte PAS
                      la validation (extension OU MIME, côté client + serveur). */}
                  <input ref={fileInputRef} type="file" accept={ACCEPT_ALL} className="hidden" onChange={handleFileChange} />
                  {/* Inputs séparés (mobile) : un seul type par input pour un menu Android clair. */}
                  <input ref={audioInputRef} type="file" accept={ACCEPT_AUDIO} className="hidden" onChange={handleFileChange} />
                  <input ref={videoInputRef} type="file" accept={ACCEPT_VIDEO} className="hidden" onChange={handleFileChange} />
                  {file ? (
                    <>
                      <FileAudio size={40} color={M_COLOR} />
                      <div style={{ textAlign: 'center' }}>
                        <p style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', margin: 0 }}>{file.name}</p>
                        <p style={{ fontSize: 12, color: '#6B7280', margin: '2px 0 0' }}>{formatFileSize(file.size)}</p>
                      </div>
                      <button type="button" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click() }}
                        style={{ fontSize: 12, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit' }}>
                        Changer de fichier
                      </button>
                    </>
                  ) : (
                    <>
                      <Upload size={40} color="#9CA3AF" />
                      {isMobile ? (
                        // MOBILE : deux choix explicites → menu Android clair (audio seul / vidéo seule).
                        <div style={{ textAlign: 'center', width: '100%' }}>
                          <p style={{ fontSize: 14, fontWeight: 500, color: '#374151', margin: 0 }}>Importez votre fichier</p>
                          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 14, flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); audioInputRef.current?.click() }}
                              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 16px', borderRadius: 9, fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', border: 'none', background: M_COLOR, color: 'white' }}
                            >
                              <Mic size={15} /> Importer un audio
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); videoInputRef.current?.click() }}
                              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 16px', borderRadius: 9, fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', border: `1.5px solid ${M_COLOR}`, background: 'white', color: M_COLOR }}
                            >
                              <FileAudio size={15} /> Importer une vidéo
                            </button>
                          </div>
                          <p style={{ fontSize: 11, color: '#6B7280', margin: '12px 0 0' }}>Tous formats acceptés — max 2 Go</p>
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center' }}>
                          <p style={{ fontSize: 14, fontWeight: 500, color: '#374151', margin: 0 }}>Glissez-déposez votre fichier ici</p>
                          <p style={{ fontSize: 12, color: '#6B7280', margin: '4px 0 0' }}>ou cliquez pour sélectionner</p>
                          <p style={{ fontSize: 11, color: '#6B7280', margin: '8px 0 0' }}>Tous formats acceptés — max 2 Go</p>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Pill « formats acceptés » — toujours visible sous l'upload */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 16px', borderRadius: 999, background: '#F3F4F6', border: '1px solid #E5E7EB', fontSize: 12, color: '#6B7280', textAlign: 'center' }}>
                    <Info size={14} style={{ color: M_COLOR, flexShrink: 0 }} />
                    <span>Formats acceptés : MP3, M4A, WAV, MP4, MOV, AAC&nbsp;&nbsp;•&nbsp;&nbsp;Taille max. : 2 Go</span>
                  </div>
                </div>

                {/* Mode selection — cartes riches (responsive : 2 col. desktop, 1 col. mobile) */}
                {file && !isTranscribing && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <p style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a', margin: 0 }}>Mode de transcription</p>
                      <HelpCircle size={15} style={{ color: '#9CA3AF' }} aria-label="Aide sur les modes de transcription" />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                      {([
                        { mode: 'simple' as const, Icon: Mic, title: 'Transcription simple', desc: 'Texte transcrit en continu, sans distinguer qui parle. Idéal pour une dictée ou un seul intervenant.' },
                        { mode: 'diarize' as const, Icon: Users, title: 'Identifier les locuteurs', desc: 'Distingue chaque intervenant et identifie qui parle. Recommandé pour les entretiens et réunions. Non disponible pour les fichiers volumineux (> 10 Mo).' },
                      ]).map(({ mode, Icon, title, desc }) => {
                        const selected = selectedMode === mode
                        return (
                          <button key={mode} type="button" onClick={() => setSelectedMode(mode)}
                            style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: 16, borderRadius: 12, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', background: selected ? M_BG : '#fff', border: selected ? `2px solid ${M_COLOR}` : '1px solid #E5E7EB', transition: 'all 0.15s' }}>
                            <div style={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: selected ? `${M_COLOR}1A` : '#F3F4F6' }}>
                              <Icon size={20} color={selected ? M_COLOR : '#9CA3AF'} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                <span style={{ fontSize: 14, fontWeight: 700, color: selected ? M_COLOR : '#1a1a1a' }}>{title}</span>
                                {selected
                                  ? <span style={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, background: M_COLOR, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Check size={14} color="#fff" /></span>
                                  : <span style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, border: '2px solid #D1D5DB' }} />}
                              </div>
                              <p style={{ fontSize: 12, color: '#6B7280', margin: '4px 0 0', lineHeight: 1.5 }}>{desc}</p>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Loading indicator */}
                {isTranscribing && (
                  <div style={{ borderRadius: 10, border: `1px solid ${M_COLOR}30`, background: M_BG, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {uploadProgress !== null && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Loader2 size={16} color={M_COLOR} className="animate-spin" />
                            <span style={{ fontSize: 14, fontWeight: 500, color: '#1a1a1a' }}>
                              {uploadProgress.pct < 100 ? 'Upload en cours...' : 'Upload terminé'}
                            </span>
                          </div>
                          <span style={{ fontSize: 14, fontWeight: 700, color: M_COLOR }}>{uploadProgress.pct}%</span>
                        </div>
                        <div style={{ height: 8, background: '#D1D5DB', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ height: '100%', background: M_COLOR, borderRadius: 4, width: `${uploadProgress.pct}%`, transition: 'width 0.3s ease-out' }} />
                        </div>
                        <p style={{ fontSize: 12, color: '#6B7280', margin: 0 }}>Envoi du fichier vers le serveur…</p>
                      </div>
                    )}
                    {uploadProgress !== null && transcriptionProgress !== null && (
                      <div style={{ borderTop: `1px solid ${M_COLOR}20` }} />
                    )}
                    {transcriptionProgress !== null && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {transcriptionProgress.pct === 100
                              ? <span style={{ fontSize: 18, lineHeight: 1 }}>✅</span>
                              : <Loader2 size={16} color={M_COLOR} className="animate-spin" />}
                            <span style={{ fontSize: 14, fontWeight: 500, color: '#1a1a1a' }}>
                              {transcriptionProgress.pct === 100 ? 'Transcription terminée !' : transcriptionProgress.message}
                            </span>
                          </div>
                          <span style={{ fontSize: 14, fontWeight: 700, color: M_COLOR }}>{transcriptionProgress.pct}%</span>
                        </div>
                        <div style={{ height: 8, background: '#D1D5DB', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ height: '100%', background: M_COLOR, borderRadius: 4, width: `${transcriptionProgress.pct}%`, transition: 'width 0.5s ease-out' }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#6B7280' }}>
                          {selectedMode === 'diarize' && transcriptionProgress.pct < 100 && (
                            <span style={{ background: `${M_COLOR}15`, color: M_COLOR, border: `1px solid ${M_COLOR}40`, borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 600 }}>Diarisation</span>
                          )}
                          <span>{transcriptionProgress.pct < 100 ? `${transcriptionProgress.pct}% complété` : 'Finalisation…'}</span>
                        </div>
                      </div>
                    )}
                    {uploadProgress === null && transcriptionProgress === null && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Loader2 size={16} color={M_COLOR} className="animate-spin" />
                        <span style={{ fontSize: 14, fontWeight: 500, color: '#1a1a1a' }}>Préparation…</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: `1px solid ${M_COLOR}20`, paddingTop: 10 }}>
                      <button type="button" onClick={handleCancelTranscribe}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', color: '#6B7280', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                        <X size={14} /> Annuler
                      </button>
                    </div>
                  </div>
                )}

                {/* Success indicator */}
                {hasRaw && !isTranscribing && (
                  <div style={{ borderRadius: 8, border: `1px solid ${M_COLOR}30`, background: `${M_COLOR}08`, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Check size={15} color={M_COLOR} />
                    <span style={{ fontSize: 13, color: M_COLOR, fontWeight: 600 }}>Transcription disponible — passez à l&apos;étape 3 pour consulter le résultat.</span>
                  </div>
                )}

                {/* Coût en crédits — plan Pro */}
                {!isTranscribing && file && (
                  <CreditCostBanner moduleType="transcription" />
                )}

                {/* Bandeau « fichier prêt » + actions — vert = lancement.
                    Mobile : empilé + boutons pleine largeur (anti-débordement).
                    Logique inchangée (reset, no-params dialog, blocage Pro). */}
                {file && !isTranscribing && (
                  <div style={{ borderRadius: 12, background: '#F0FDF4', border: '1px solid #BBF7D0', padding: 16, display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', gap: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                      <div style={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0, background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Sparkles size={20} color="#fff" />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ fontSize: 14, fontWeight: 700, color: '#15803D', margin: 0 }}>Votre fichier est prêt&nbsp;!</p>
                        <p style={{ fontSize: 13, color: '#6B7280', margin: '2px 0 0' }}>Choisissez un mode et lancez la transcription.</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
                      <button onClick={handleReset} disabled={isCorrecting}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 16px', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', border: '1px solid #D1D5DB', borderRadius: 9, background: 'white', color: '#374151', opacity: isCorrecting ? 0.5 : 1, flex: isMobile ? 1 : 'none' }}>
                        <RotateCcw size={14} /> Nouveau projet
                      </button>
                      {transcriptionBlocked ? (
                        <UpgradeBlockedCTA
                          variant={transcriptionExhausted ? 'exhausted' : 'locked'}
                          onUpgrade={openUpgrade}
                          align="flex-end"
                        />
                      ) : (
                        <button
                          onClick={() => {
                            // À l'étape 1, avertir si aucun paramètre d'entretien renseigné.
                            // Si une transcription existe déjà (Relancer), pas d'avertissement.
                            if (!hasRaw && !hasFilledParams) {
                              setShowNoParamsDialog(true)
                              return
                            }
                            void handleTranscribe()
                          }}
                          disabled={isCorrecting}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '10px 20px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: isCorrecting ? 0.5 : 1, boxShadow: '0 2px 8px rgba(22,163,74,.3)', flex: isMobile ? 1 : 'none' }}>
                          <Mic size={18} />
                          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.15 }}>
                            <span>{hasRaw ? 'Relancer' : selectedMode === 'diarize' ? 'Lancer la diarisation' : 'Lancer la transcription'}</span>
                            <span style={{ fontSize: 11, fontWeight: 500, opacity: 0.9 }}>Démarrer maintenant</span>
                          </span>
                          <ArrowRight size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Historique récent (48 h) ─────────────────────────── */}
                {isAuthenticated && !isTranscribing && history.length > 0 && (
                  <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{ padding: '10px 14px', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: 7 }}>
                      <Clock size={14} color={M_COLOR} />
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Historique récent</span>
                      <span style={{ fontSize: 11, color: '#6B7280' }}>— 48 dernières heures</span>
                    </div>
                    <div>
                      {history.map((h, idx) => (
                        <div key={h.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 14px', borderBottom: idx < history.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <p style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {h.file_name || 'Transcription'}
                            </p>
                            <p style={{ fontSize: 11, color: '#6B7280', margin: '2px 0 0' }}>
                              {new Date(h.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              {typeof h.word_count === 'number' ? ` · ${formatNumber(h.word_count)} mots` : ''}
                            </p>
                          </div>
                          <button
                            onClick={() => handleDownloadHistory(h)}
                            disabled={!h.texte}
                            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 11px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: h.texte ? 'pointer' : 'not-allowed', border: `1px solid ${M_COLOR}50`, borderRadius: 7, background: `${M_COLOR}10`, color: M_COLOR, flexShrink: 0, opacity: h.texte ? 1 : 0.5 }}
                          >
                            <FileDown size={12} /> Word
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── STEP 2 : Paramètres ───────────────────────────────────── */}
            {step === 2 && (
              <div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a', marginBottom: 4 }}>Paramètres de l&apos;entretien</div>
                  <div style={{ fontSize: 12, color: '#6B7280' }}>Champs optionnels — apparaissent dans l&apos;en-tête du document Word exporté.</div>
                </div>
                <div style={{ background: M_BG, border: `1px solid ${M_COLOR}30`, borderRadius: 10, padding: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px' }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: M_COLOR, display: 'block', marginBottom: 3 }}>Projet / étude</label>
                      <input value={interviewParams.projet} onChange={setParam('projet')} placeholder="Ex : Étude accès eau potable"
                        style={{ width: '100%', height: 28, padding: '0 8px', fontSize: 12, border: `1px solid ${M_COLOR}30`, borderRadius: 6, background: 'white', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: M_COLOR, display: 'block', marginBottom: 3 }}>Code répondant</label>
                      <input value={interviewParams.repondant} onChange={setParam('repondant')} placeholder="Ex : Répondant 03"
                        style={{ width: '100%', height: 28, padding: '0 8px', fontSize: 12, border: `1px solid ${M_COLOR}30`, borderRadius: 6, background: 'white', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: M_COLOR, display: 'block', marginBottom: 3 }}>Statut / profil</label>
                      <input value={interviewParams.statut} onChange={setParam('statut')} placeholder="Ex : Chef de village"
                        style={{ width: '100%', height: 28, padding: '0 8px', fontSize: 12, border: `1px solid ${M_COLOR}30`, borderRadius: 6, background: 'white', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: M_COLOR, display: 'block', marginBottom: 3 }}>Ville</label>
                      <select value={interviewParams.localite} onChange={setParam('localite')}
                        style={{ width: '100%', height: 28, padding: '0 8px', fontSize: 12, border: `1px solid ${M_COLOR}30`, borderRadius: 6, background: 'white', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' }}>
                        <option value="">— Sélectionner —</option>
                        <optgroup label="Côte d'Ivoire">
                          <option>Abidjan</option><option>Bouaké</option><option>Yamoussoukro</option>
                          <option>San-Pédro</option><option>Daloa</option><option>Korhogo</option>
                          <option>Man</option><option>Gagnoa</option><option>Abengourou</option><option>Divo</option>
                        </optgroup>
                        <optgroup label="International">
                          <option>Paris</option><option>Québec</option><option>Montréal</option>
                          <option>Dakar</option><option>Bruxelles</option>
                        </optgroup>
                        <option value="Autre">Autre</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: M_COLOR, display: 'block', marginBottom: 3 }}>Date</label>
                      <input type="date" value={interviewParams.dateEntretien} onChange={setParam('dateEntretien')}
                        style={{ width: '100%', height: 28, padding: '0 8px', fontSize: 12, border: `1px solid ${M_COLOR}30`, borderRadius: 6, background: 'white', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: M_COLOR, display: 'block', marginBottom: 3 }}>Durée</label>
                      <input value={interviewParams.duree} onChange={setParam('duree')} placeholder="Ex : 45 min"
                        style={{ width: '100%', height: 28, padding: '0 8px', fontSize: 12, border: `1px solid ${M_COLOR}30`, borderRadius: 6, background: 'white', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: M_COLOR, display: 'block', marginBottom: 3 }}>Enquêteur/rice</label>
                      <input value={interviewParams.enqueteur} onChange={setParam('enqueteur')} placeholder="Ex : Marie Dupont"
                        style={{ width: '100%', height: 28, padding: '0 8px', fontSize: 12, border: `1px solid ${M_COLOR}30`, borderRadius: 6, background: 'white', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: M_COLOR, display: 'block', marginBottom: 3 }}>Langue</label>
                      <select value={interviewParams.langue} onChange={setParam('langue')}
                        style={{ width: '100%', height: 28, padding: '0 8px', fontSize: 12, border: `1px solid ${M_COLOR}30`, borderRadius: 6, background: 'white', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' }}>
                        <option>Français</option>
                        <option>Français + dialecte local</option>
                        <option>Autre</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 3 : Résultat ─────────────────────────────────────── */}
            {step === 3 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {!hasRaw ? (
                  <div style={{ borderRadius: 8, border: '1.5px dashed #E5E7EB', background: '#FAFAFA', padding: 24, textAlign: 'center' }}>
                    {file ? (
                      <>
                        <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 14px 0' }}>
                          {isTranscribing
                            ? 'Transcription en cours, merci de patienter…'
                            : 'Tous vos paramètres sont prêts. Lancez la transcription pour générer le verbatim.'}
                        </p>
                        <button
                          className="qly-launch-btn"
                          onClick={() => {
                            // Bascule vers l'étape 1 où l'UI riche de progression
                            // (upload bar + transcription bar + messages d'état) est
                            // affichée. handleTranscribe basculera automatiquement
                            // vers l'étape 3 à la fin via setStep(3).
                            setStep(1)
                            void handleTranscribe()
                          }}
                          disabled={isTranscribing || transcriptionBlocked}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '11px 22px',
                            background: isTranscribing ? '#0f3a5e' : M_COLOR,
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 13,
                            fontWeight: 700,
                            cursor: isTranscribing || transcriptionBlocked ? 'not-allowed' : 'pointer',
                            fontFamily: 'inherit',
                            opacity: transcriptionBlocked ? 0.5 : 1,
                            boxShadow: isTranscribing ? `0 0 0 3px ${M_COLOR}25` : `0 2px 8px ${M_COLOR}40`,
                            transition: 'transform .08s ease, box-shadow .15s ease, background .15s ease',
                          }}
                        >
                          {isTranscribing ? (
                            <>
                              <Loader2 size={15} className="animate-spin" />
                              {selectedMode === 'diarize' ? 'Diarisation en cours…' : 'Transcription en cours…'}
                            </>
                          ) : (
                            <>
                              <Mic size={15} />
                              {selectedMode === 'diarize' ? 'Lancer la diarisation' : 'Lancer la transcription'}
                            </>
                          )}
                        </button>
                        {/* Effet de press visuel — feedback immédiat au clic */}
                        <style>{`
                          .qly-launch-btn:active:not(:disabled) {
                            transform: scale(0.97);
                            box-shadow: 0 1px 3px ${M_COLOR}60 inset !important;
                          }
                          .qly-launch-btn:hover:not(:disabled) {
                            box-shadow: 0 4px 14px ${M_COLOR}55 !important;
                          }
                        `}</style>
                      </>
                    ) : (
                      <p style={{ fontSize: 13, color: '#6B7280', margin: 0 }}>
                        Aucun fichier audio sélectionné. Retournez à l&apos;étape 1 pour en charger un.
                      </p>
                    )}
                  </div>
                ) : (
                  <>
                    <div style={{ borderRadius: 8, border: `1px solid ${M_COLOR}30`, background: `${M_COLOR}08`, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Check size={18} color={M_COLOR} />
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>Transcription terminée</div>
                        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                          {formatNumber(countWords(rawTranscription))} mots · {formatNumber(rawTranscription.length)} car. — verbatim et correction affichés ci-dessous
                        </div>
                      </div>
                    </div>
                    <div style={{ borderRadius: 10, border: `1.5px dashed ${M_COLOR}40`, background: `${M_COLOR}06`, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ fontSize: 14, color: '#1a1a1a' }}>Transcrire un autre entretien&nbsp;?</span>
                      <button onClick={handleReset}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: M_COLOR, color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>
                        <Upload size={13} /> Nouvel audio / vidéo
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

          </div>

          {/* ── Bottom nav ─────────────────────────────────────────────────── */}
          <div className="qly-nav-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 28px', borderTop: '1px solid #E5E7EB', background: '#FAFAF8' }}>
            <button
              disabled={step === 1}
              onClick={() => setStep(s => s - 1)}
              style={{ padding: '10px 20px', background: 'transparent', color: step === 1 ? '#D1D5DB' : '#6B7280', border: `1.5px solid ${step === 1 ? '#E5E7EB' : '#D1D5DB'}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: step === 1 ? 'default' : 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}
            >
              ← Retour
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 12, color: '#6B7280' }}>Étape {step} sur {M_STEPS.length}</span>
              {hasRaw && (
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={isTranscribing || isCorrecting}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#fff', color: M_COLOR, border: `1.5px solid ${M_COLOR}`, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: (isTranscribing || isCorrecting) ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: (isTranscribing || isCorrecting) ? 0.5 : 1 }}
                >
                  <RotateCcw size={13} /> Nouveau projet
                </button>
              )}
            </div>
            {step < M_STEPS.length ? (
              <button
                onClick={() => setStep(s => s + 1)}
                style={{ padding: '10px 24px', background: M_COLOR, color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8, boxShadow: `0 2px 8px ${M_COLOR}40`, transition: 'background 0.15s' }}
              >
                Continuer →
              </button>
            ) : <div style={{ width: 110 }} />}
          </div>
        </div>

        </div>{/* fin stepper wrapper */}
        </div>{/* fin top section */}

        {/* Bandeau résultat précédent restauré (Module 02 — Transcription) */}
        {restoredFromCache && hasRaw && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 14px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, fontSize: 13, color: '#1E40AF', marginBottom: 12 }}>
            <span>Résultat précédent disponible — verbatim et paramètres restaurés.</span>
            <button onClick={dismissRestoredBanner} style={{ background: 'transparent', border: '1px solid #BFDBFE', color: '#1E40AF', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Effacer</button>
          </div>
        )}

        {/* ── Bloc transcription pleine largeur ──────────────────────── */}
        {hasRaw && (
          <div style={{ background: 'white', border: `1px solid ${M_COLOR}30`, borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '18px 24px', borderBottom: `1px solid ${M_COLOR}15`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: M_BG, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 8, background: M_COLOR, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FileAudio size={17} color="white" />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>Verbatim brut</div>
                  <div style={{ fontSize: 11, color: '#6B7280' }}>{formatNumber(countWords(rawTranscription))} mots · {formatNumber(rawTranscription.length)} car.</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {savedTranscriptionId && (
                  <span style={{ fontSize: 10, fontWeight: 600, background: `${M_COLOR}15`, color: M_COLOR, border: `1px solid ${M_COLOR}40`, borderRadius: 4, padding: '2px 7px', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <Save size={10} /> {autoSaved ? 'Sauvegardé automatiquement' : 'Sauvegardé'}
                  </span>
                )}
                {/* Re-sauvegarde après édition du verbatim — met à jour la
                    ligne auto-sauvegardée (pas de doublon). */}
                <button onClick={handleSave} disabled={isSaving}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 11px', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', cursor: isSaving ? 'wait' : 'pointer', border: '1px solid #D1D5DB', borderRadius: 7, background: 'white', color: '#374151', opacity: isSaving ? 0.6 : 1 }}>
                  {isSaving ? <><Loader2 size={12} className="animate-spin" /> …</> : <><Save size={12} /> Enregistrer</>}
                </button>
                <button onClick={() => handleCopy(editableText, setCopiedEditable, 'Texte')}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 11px', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', border: '1px solid #D1D5DB', borderRadius: 7, background: 'white', color: '#374151' }}>
                  {copiedEditable ? <><Check size={12} /> Copié !</> : <><Copy size={12} /> Copier</>}
                </button>
                <button onClick={() => handleDownloadTxt(editableText, 'transcription')}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 11px', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', border: '1px solid #D1D5DB', borderRadius: 7, background: 'white', color: '#374151' }}>
                  <Download size={12} /> .txt
                </button>
                <button onClick={() => handleDownloadWord(editableText, 'transcription')}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 11px', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', border: `1px solid ${M_COLOR}50`, borderRadius: 7, background: `${M_COLOR}10`, color: M_COLOR }}>
                  <FileDown size={12} /> Word (.doc)
                </button>
              </div>
            </div>
            <div style={{ padding: '20px 24px' }}>
              {diarizedSegments.length > 0 ? (
                <div className="custom-scrollbar" style={{ maxHeight: 480, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {diarizedSegments.map((seg, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 12 }}>
                      <div style={{ flexShrink: 0, width: 96 }}>
                        <span style={{
                          fontSize: 10, fontWeight: 600, borderRadius: 4, padding: '1px 6px', display: 'inline-block',
                          ...(seg.speaker === 'Locuteur 1' ? { background: '#ecfdf5', color: '#065f46', border: '1px solid #6ee7b7' }
                            : seg.speaker === 'Locuteur 2' ? { background: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74' }
                            : seg.speaker === 'Locuteur 3' ? { background: '#f5f3ff', color: '#6d28d9', border: '1px solid #c4b5fd' }
                            : { background: '#eff6ff', color: '#1e40af', border: '1px solid #93c5fd' }),
                        }}>
                          {seg.speaker}
                        </span>
                        <p style={{ fontSize: 10, color: '#6B7280', margin: '2px 0 0' }}>
                          {formatDuration(seg.start)} → {formatDuration(seg.end)}
                        </p>
                      </div>
                      <p style={{ flex: 1, fontSize: 14, lineHeight: 1.6, color: '#1a1a1a', margin: 0 }}>{seg.text}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <Textarea
                  value={editableText}
                  onChange={(e) => setEditableText(e.target.value)}
                  className="custom-scrollbar"
                  style={{ minHeight: 260, maxHeight: 520, resize: 'vertical', fontSize: 14, lineHeight: '1.6' }}
                  placeholder="Le texte transcrit apparaîtra ici…"
                />
              )}
            </div>
          </div>
        )}

        {/* ── Bloc correction ────────────────────────────────────────── */}
        {hasRaw && (
          <div style={{ background: 'white', border: `1px solid ${M_COLOR}30`, borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '18px 24px', borderBottom: `1px solid ${M_COLOR}15`, background: M_BG, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: M_COLOR, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <PencilLine size={17} color="white" />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>Correction du verbatim</div>
                <div style={{ fontSize: 11, color: '#6B7280' }}>Correction automatique par Qualyse IA</div>
              </div>
            </div>
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Contexte */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Paperclip size={13} color="#9CA3AF" />
                  <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>Contexte et consignes</p>
                </div>
                <div style={{ borderRadius: 8, border: '1px solid #E5E7EB', background: '#F9FAFB' }}
                  onDragEnter={handleContextDragEnter} onDragLeave={handleContextDragLeave}
                  onDragOver={handleContextDragOver} onDrop={handleContextDrop}>
                  <Textarea
                    value={contexte}
                    onChange={(e) => setContexte(e.target.value)}
                    style={{ minHeight: 80, maxHeight: 180, resize: 'vertical', fontSize: 14, lineHeight: '1.6', background: 'transparent', border: 'none', boxShadow: 'none', borderRadius: '8px 8px 0 0' }}
                    placeholder="Contexte de recherche, termes spécifiques, noms propres, guide d'entretien…"
                  />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderTop: '1px solid #E5E7EB' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {contextFiles.map((f) => (
                        <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 4, border: '1px solid #E5E7EB', borderRadius: 4, padding: '2px 6px', background: 'white' }}>
                          <span style={{ fontSize: 11, color: '#374151', fontWeight: 500 }}>{f.name}</span>
                          <button onClick={() => removeContextFile(f.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#6B7280', display: 'flex', alignItems: 'center' }} aria-label={`Retirer ${f.name}`}>
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                      {isParsingFiles && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#6B7280' }}>
                          <Loader2 size={12} className="animate-spin" /> Extraction…
                        </span>
                      )}
                    </div>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" onClick={() => contextFileInputRef.current?.click()}
                          style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                          <Paperclip size={13} /> Joindre
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">PDF, DOCX, XLSX, TXT — max {MAX_CONTEXT_FILES} fichiers</TooltipContent>
                    </Tooltip>
                    <input ref={contextFileInputRef} type="file" multiple accept={CONTEXT_ACCEPT_ATTR} className="hidden" onChange={handleContextFileChange} />
                  </div>
                </div>
              </div>

              {/* Bouton correction */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {correctionBlocked ? (
                  <UpgradeBlockedCTA variant="locked" onUpgrade={openUpgrade} align="flex-start" />
                ) : (
                  <>
                    <button onClick={handleCorrect} disabled={isCorrecting}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: M_COLOR, color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: isCorrecting ? 0.7 : 1 }}>
                      {isCorrecting ? <><Loader2 size={15} className="animate-spin" /> Correction en cours…</> : <><PencilLine size={15} /> Corriger le verbatim</>}
                    </button>
                    {hasAnyContext && (
                      <span style={{ fontSize: 11, color: M_COLOR, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Check size={12} /> Contexte fourni
                      </span>
                    )}
                  </>
                )}
              </div>

              {/* Résultat corrigé */}
              {hasCorrected && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Check size={16} color={M_COLOR} />
                      <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>Verbatim corrigé</span>
                    </div>
                    <p style={{ fontSize: 12, color: '#6B7280', margin: 0 }}>{formatNumber(correctedText.length)} car. · {formatNumber(countWords(correctedText))} mots</p>
                  </div>
                  <Textarea
                    value={correctedText}
                    onChange={(e) => setCorrectedText(e.target.value)}
                    className="custom-scrollbar"
                    style={{ minHeight: 260, maxHeight: 520, resize: 'vertical', fontSize: 14, lineHeight: '1.6' }}
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                    <button onClick={() => handleCopy(correctedText, setCopiedCorrected, 'Verbatim corrigé')}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', border: '1px solid #D1D5DB', borderRadius: 7, background: 'white', color: '#374151' }}>
                      {copiedCorrected ? <><Check size={13} /> Copié !</> : <><Copy size={13} /> Copier</>}
                    </button>
                    <button onClick={() => handleDownloadTxt(correctedText, 'verbatim_corrige')}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', border: '1px solid #D1D5DB', borderRadius: 7, background: 'white', color: '#374151' }}>
                      <Download size={13} /> .txt
                    </button>
                    <button onClick={() => handleDownloadWord(correctedText, 'verbatim_corrige')}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', border: `1px solid ${M_COLOR}50`, borderRadius: 7, background: `${M_COLOR}10`, color: M_COLOR }}>
                      <FileDown size={13} /> Word (.doc)
                    </button>
                  </div>
                </div>
              )}

              {/* RGPD */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, borderRadius: 8, border: '1px solid #E5E7EB', background: 'white', padding: '12px 16px' }}>
                <ShieldCheck size={16} color={M_COLOR} style={{ flexShrink: 0 }} />
                <p style={{ fontSize: 11, color: '#6B7280', lineHeight: 1.6, margin: 0 }}>
                  Aucune donnée n&apos;est stockée ni partagée avec des tiers. Conforme RGPD.
                </p>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* No context dialog */}
      <AlertDialog open={showNoContextDialog} onOpenChange={setShowNoContextDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Pas de contexte fourni</AlertDialogTitle>
            <AlertDialogDescription>
              La correction Qualyse fonctionne mieux avec un contexte de recherche (thème, termes spécifiques, noms propres…).
              Voulez-vous continuer sans contexte ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleProceedWithoutContext}>
              Continuer sans contexte
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* No interview params dialog */}
      <AlertDialog open={showNoParamsDialog} onOpenChange={setShowNoParamsDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Aucun paramètre d&apos;entretien renseigné</AlertDialogTitle>
            <AlertDialogDescription>
              Vous pouvez renseigner les coordonnées et paramètres de l&apos;entretien (projet, répondant, localité, durée…) avant de lancer la transcription.
              Voulez-vous continuer sans paramètres ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowNoParamsDialog(false); setStep(2) }}>
              Ajouter les paramètres
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowNoParamsDialog(false); void handleTranscribe() }}>
              Lancer sans paramètres
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  )
}
